import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import {
  Plus,
  FolderPlus,
  X,
  Folder,
  ChevronRight,
  ChevronDown,
  MoreHorizontal,
  Pencil,
  GripVertical,
  Trash2,
  Settings,
  Brain,
  FileText,
  LogOut,
  Search,
  Loader2,
  CreditCard,
  EyeOff,
  User,
  BookOpen,
  Mail,
  Bell,
  Target
} from 'lucide-react';

const Sidebar = ({
  showSidebar,
  setShowSidebar,
  conversations,
  folders,
  currentConversationId,
  expandedFolders,
  setExpandedFolders,
  editingChatId,
  setEditingChatId,
  editingChatTitle,
  setEditingChatTitle,
  editingFolderId,
  setEditingFolderId,
  editingFolderName,
  setEditingFolderName,
  newFolderName,
  setNewFolderName,
  showNewFolder,
  setShowNewFolder,
  chatMenuId,
  setChatMenuId,
  draggedChat,
  dropTarget,
  showSettingsMenu,
  setShowSettingsMenu,
  searchQuery,
  setSearchQuery,
  searchResults,
  searching,
  showSearch,
  setShowSearch,
  isTemporaryMode,
  // Operations
  switchConversation,
  createNewConversation,
  startTemporaryChat,
  renameChat,
  deleteConversation,
  createFolder,
  renameFolder,
  deleteFolder,
  handleDragStart,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleLogout,
  setShowMemories,
  setShowDocuments,
  setShowProfile,
  setShowFocus
}) => {
  const navigate = useNavigate();
  const unfiledChats = conversations.filter((c) => !c.folder_id);
  const getChatsByFolder = (folderId) => conversations.filter((c) => c.folder_id === folderId);

  const renderChatItem = (conv) => (
    <div
      key={conv.id}
      draggable
      onDragStart={(e) => handleDragStart(e, conv.id)}
      className={`group relative ${draggedChat === conv.id ? 'opacity-50' : ''}`}
    >
      {editingChatId === conv.id ? (
        <div className="p-2">
          <Input
            value={editingChatTitle}
            onChange={(e) => setEditingChatTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') renameChat(conv.id);
              if (e.key === 'Escape') {
                setEditingChatId(null);
                setEditingChatTitle('');
              }
            }}
            onBlur={() => renameChat(conv.id)}
            autoFocus
            className="h-8 text-sm"
          />
        </div>
      ) : (
        <div
          onClick={() => switchConversation(conv.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && switchConversation(conv.id)}
          className={`w-full text-left p-2.5 rounded-lg transition-colors flex items-center gap-2 cursor-pointer ${
            currentConversationId === conv.id ? 'bg-muted' : 'hover:bg-muted/50'
          }`}
          data-testid={`chat-item-${conv.id}`}
        >
          <GripVertical className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 cursor-grab flex-shrink-0" />
          <div className="flex-1 min-w-0 overflow-hidden">
            <p className="text-sm font-medium truncate">{conv.title || 'New chat'}</p>
            <p className="text-xs text-muted-foreground line-clamp-2">{conv.preview}</p>
          </div>
          <div className="opacity-0 group-hover:opacity-100 relative flex-shrink-0" data-chat-menu>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setChatMenuId(chatMenuId === conv.id ? null : conv.id);
              }}
              className="p-1 hover:bg-muted rounded"
            >
              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
            </button>
            {chatMenuId === conv.id && (
              <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[120px] z-50">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingChatId(conv.id);
                    setEditingChatTitle(conv.title || '');
                    setChatMenuId(null);
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center gap-2"
                >
                  <Pencil className="h-3 w-3" /> Rename
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteConversation(conv.id);
                    setChatMenuId(null);
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted text-destructive flex items-center gap-2"
                >
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <AnimatePresence>
      {showSidebar && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-20 lg:hidden"
            onClick={() => setShowSidebar(false)}
          />

          <motion.aside
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed left-0 top-0 bottom-0 w-72 bg-card border-r border-border z-30 flex flex-col"
            data-testid="sidebar"
          >
            {/* Sidebar Header */}
            <div className="p-3 border-b border-border flex items-center justify-between">
              <span className="font-medium text-sm">Chats</span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowNewFolder(true)}
                  className="h-8 w-8 p-0"
                  title="New folder"
                >
                  <FolderPlus className="h-4 w-4" />
                </Button>

                {/* Settings Menu */}
                <div className="relative" data-settings-menu>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                    className="h-8 w-8 p-0"
                    title="Settings"
                    data-testid="settings-btn"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                  {showSettingsMenu && (
                    <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[160px] z-50">
                      <button
                        onClick={() => {
                          setShowProfile(true);
                          setShowSettingsMenu(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
                        data-testid="profile-menu-btn"
                      >
                        <User className="h-4 w-4 text-emerald-400" /> Profile
                      </button>
                      <button
                        onClick={() => {
                          setShowMemories(true);
                          setShowSettingsMenu(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
                        data-testid="memories-menu-btn"
                      >
                        <Brain className="h-4 w-4 text-violet-400" /> Memories
                      </button>
                      <button
                        onClick={() => {
                          setShowFocus(true);
                          setShowSettingsMenu(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
                        data-testid="focus-menu-btn"
                      >
                        <Target className="h-4 w-4 text-orange-400" /> Current Focus
                      </button>
                      <button
                        onClick={() => {
                          setShowDocuments(true);
                          setShowSettingsMenu(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
                        data-testid="knowledge-menu-btn"
                      >
                        <FileText className="h-4 w-4 text-blue-400" /> Knowledge Base
                      </button>
                      <button
                        onClick={() => {
                          navigate('/pricing');
                          setShowSettingsMenu(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
                        data-testid="billing-menu-btn"
                      >
                        <CreditCard className="h-4 w-4 text-emerald-400" /> Billing
                      </button>
                      <button
                        onClick={() => {
                          navigate('/docs');
                          setShowSettingsMenu(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
                        data-testid="docs-menu-btn"
                      >
                        <BookOpen className="h-4 w-4 text-sky-400" /> Documentation
                      </button>
                      <div className="border-t border-border my-1" />
                      <button
                        onClick={() => {
                          handleLogout();
                          setShowSettingsMenu(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted text-destructive flex items-center gap-2"
                        data-testid="logout-menu-btn"
                      >
                        <LogOut className="h-4 w-4" /> Sign out
                      </button>
                    </div>
                  )}
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSidebar(false)}
                  className="h-8 w-8 p-0 lg:hidden"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="px-2 pb-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setShowSearch(true)}
                  placeholder="Search conversations..."
                  className="h-8 text-sm pl-8 pr-8"
                  data-testid="search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setShowSearch(false);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Search Results Dropdown */}
              <AnimatePresence>
                {showSearch && searchQuery && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="absolute left-2 right-2 top-full mt-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-[300px] overflow-y-auto"
                  >
                    {searching ? (
                      <div className="p-3 text-center text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                        Searching...
                      </div>
                    ) : searchResults.length === 0 ? (
                      <div className="p-3 text-center text-sm text-muted-foreground">
                        No results found
                      </div>
                    ) : (
                      <div className="py-1">
                        {searchResults.map((result) => (
                          <button
                            key={result.conversation_id}
                            onClick={() => {
                              switchConversation(result.conversation_id);
                              setSearchQuery('');
                              setShowSearch(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-muted transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm truncate">{result.title}</span>
                              {result.title_match && (
                                <span className="text-[10px] bg-violet-500/20 text-violet-400 px-1.5 py-0.5 rounded">
                                  Title match
                                </span>
                              )}
                            </div>
                            {result.snippet && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {result.snippet}
                              </p>
                            )}
                            <div className="text-[10px] text-muted-foreground mt-1">
                              {result.message_matches} message{result.message_matches !== 1 ? 's' : ''}{' '}
                              matched
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* New Chat Button */}
            <div className="px-2 pb-2 space-y-1.5">
              <Button
                onClick={() => createNewConversation()}
                className="w-full justify-start gap-2"
                variant="outline"
                size="sm"
                data-testid="new-chat-btn"
              >
                <Plus className="h-4 w-4" /> New chat
              </Button>
              <Button
                onClick={startTemporaryChat}
                className={`w-full justify-start gap-2 ${isTemporaryMode ? 'bg-amber-500/10 border-amber-500/30 text-amber-500' : ''}`}
                variant="outline"
                size="sm"
                data-testid="temp-chat-btn"
              >
                <EyeOff className="h-4 w-4" /> {isTemporaryMode ? 'Temporary chat active' : 'Temporary chat'}
              </Button>
            </div>

            {/* Separator */}
            <div className="px-2 pb-2">
              <div className="border-t border-border/50" />
            </div>

            {/* Emails & Reminders Section */}
            <div className="px-2 pb-2 space-y-1.5">
              <Button
                onClick={() => navigate('/emails')}
                className="w-full justify-start gap-2"
                variant="outline"
                size="sm"
                data-testid="emails-nav-btn"
              >
                <Mail className="h-4 w-4" /> Emails
              </Button>
              <Button
                onClick={() => navigate('/reminders')}
                className="w-full justify-start gap-2"
                variant="outline"
                size="sm"
                data-testid="reminders-nav-btn"
              >
                <Bell className="h-4 w-4" /> Reminders
              </Button>
            </div>

            {/* New Folder Input */}
            {showNewFolder && (
              <div className="px-2 pb-2">
                <div className="flex gap-2">
                  <Input
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Folder name..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') createFolder();
                      if (e.key === 'Escape') {
                        setShowNewFolder(false);
                        setNewFolderName('');
                      }
                    }}
                    autoFocus
                    className="h-8 text-sm"
                  />
                  <Button size="sm" onClick={createFolder} disabled={!newFolderName.trim()}>
                    Add
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowNewFolder(false);
                      setNewFolderName('');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Conversations List */}
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {/* Folders */}
                {folders.map((folder) => (
                  <div
                    key={folder.id}
                    onDragOver={(e) => handleDragOver(e, folder.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, folder.id)}
                    className={`rounded-lg transition-colors ${
                      dropTarget === folder.id ? 'bg-accent/10' : ''
                    }`}
                  >
                    {/* Folder Header */}
                    <div className="flex items-center gap-1 p-2 group">
                      <button
                        onClick={() =>
                          setExpandedFolders((prev) => ({
                            ...prev,
                            [folder.id]: !prev[folder.id]
                          }))
                        }
                        className="p-0.5 hover:bg-muted rounded"
                      >
                        {expandedFolders[folder.id] ? (
                          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                      </button>
                      <Folder className="h-4 w-4 text-muted-foreground" />

                      {editingFolderId === folder.id ? (
                        <Input
                          value={editingFolderName}
                          onChange={(e) => setEditingFolderName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') renameFolder(folder.id);
                            if (e.key === 'Escape') {
                              setEditingFolderId(null);
                              setEditingFolderName('');
                            }
                          }}
                          onBlur={() => renameFolder(folder.id)}
                          autoFocus
                          className="h-6 text-xs flex-1"
                        />
                      ) : (
                        <span className="text-sm flex-1 truncate">{folder.name}</span>
                      )}

                      <div className="opacity-0 group-hover:opacity-100 flex gap-0.5">
                        <button
                          onClick={() => createNewConversation(folder.id)}
                          className="p-1 hover:bg-muted rounded"
                          title="New chat in folder"
                        >
                          <Plus className="h-3 w-3 text-muted-foreground" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingFolderId(folder.id);
                            setEditingFolderName(folder.name);
                          }}
                          className="p-1 hover:bg-muted rounded"
                          title="Rename folder"
                        >
                          <Pencil className="h-3 w-3 text-muted-foreground" />
                        </button>
                        <button
                          onClick={() => deleteFolder(folder.id)}
                          className="p-1 hover:bg-muted rounded"
                          title="Delete folder"
                        >
                          <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                        </button>
                      </div>
                    </div>

                    {/* Folder Chats */}
                    {expandedFolders[folder.id] && (
                      <div className="pl-6 space-y-0.5">
                        {getChatsByFolder(folder.id).map(renderChatItem)}
                        {getChatsByFolder(folder.id).length === 0 && (
                          <p className="text-xs text-muted-foreground py-2 px-2">Empty folder</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Unfiled Chats */}
                {unfiledChats.length > 0 && (
                  <div
                    onDragOver={(e) => handleDragOver(e, 'unfiled')}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, null)}
                    className={`space-y-0.5 ${
                      dropTarget === 'unfiled' ? 'bg-accent/10 rounded-lg p-1' : ''
                    }`}
                  >
                    {folders.length > 0 && (
                      <p className="text-xs text-muted-foreground px-2 pt-2">Unfiled</p>
                    )}
                    {unfiledChats.map(renderChatItem)}
                  </div>
                )}

                {conversations.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">No chats yet</p>
                )}
              </div>
            </ScrollArea>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default Sidebar;
